import cloneDeep from 'lodash.clonedeep'
import { set as nestedSet } from 'nested-property'
import { getAssessmentRedFlags } from '../../fields/assessment/redFlags'
import { getAssessmentStatus } from '../../fields/assessment/status'
import { getInsuranceCallInComplete, getInsuranceCallInRequired } from '../../fields/callIn/form'
import { PopulatedAssessment, PopulatedUser } from '../../types/app/populated'
import {
  AdminAssessmentData,
  Assessment,
  AssessmentSnippet,
  PracticeAssessmentData,
} from '../../types/assessments/assessment'
import { InsuranceProvider } from '../../types/insurance/insurer'
import { PracticeWithAdmin } from '../../types/providers'
import { FieldMapValue } from '../../types/utils/common'
import { mergeData, objectToArray } from '../data'
import { populatePregnancyPlans } from '../insurance'
import { getIsGynProfile } from './gyn'
import { getMidwifeId } from './midwife'
import { mergeAssessmentSignOnData } from './signOn'
import { getUrgentString } from './urgent'

const populateAssessmentData = (
  data: FieldMapValue | undefined,
  providers: Record<string, PracticeWithAdmin>,
  insurers: Record<string, InsuranceProvider>,
) => {
  if (!data) return undefined
  const populated = cloneDeep(data)
  const midwifeId = data.delivery?.midwifeId
  if (populated.delivery?.midwife) delete populated.delivery.midwife
  if (populated.insuranceInfo?.primaryCoverage?.insuranceProvider) {
    delete populated.insuranceInfo.primaryCoverage.insuranceProvider
  }
  if (populated.insuranceInfo?.secondaryCoverage?.insuranceProvider) {
    delete populated.insuranceInfo.secondaryCoverage.insuranceProvider
  }
  if (populated.insuranceInfo?.medicaidCoverage?.insuranceProvider) {
    delete populated.insuranceInfo.medicaidCoverage.insuranceProvider
  }
  const primaryInsurerId = data.insuranceInfo?.primaryCoverage?.insuranceProviderId
  const secondaryInsurerId = data.insuranceInfo?.secondaryCoverage?.insuranceProviderId
  const medicaidInsurerId = data.insuranceInfo?.medicaidCoverage?.insuranceProviderId

  if (midwifeId && providers[midwifeId]) {
    nestedSet(populated, 'delivery.midwife', providers[midwifeId])
  }
  if (primaryInsurerId && insurers[primaryInsurerId]) {
    nestedSet(
      populated,
      'insuranceInfo.primaryCoverage.insuranceProvider',
      insurers[primaryInsurerId],
    )
  }
  if (secondaryInsurerId && insurers[secondaryInsurerId]) {
    nestedSet(
      populated,
      'insuranceInfo.secondaryCoverage.insuranceProvider',
      insurers[secondaryInsurerId],
    )
  }
  if (medicaidInsurerId && insurers[medicaidInsurerId]) {
    nestedSet(
      populated,
      'insuranceInfo.medicaidCoverage.insuranceProvider',
      insurers[medicaidInsurerId],
    )
  }

  return populated
}

export const populateAssessment = (
  user: PopulatedUser,
  assessment: Assessment,
  providers: Record<string, PracticeWithAdmin>,
  insurers: Record<string, InsuranceProvider>,
  assessmentSnippet?: AssessmentSnippet | null,
  adminAssessmentData?: AdminAssessmentData | null,
  practiceAssessmentData?: PracticeAssessmentData | null,
): PopulatedAssessment => {
  const populatedData = populateAssessmentData(assessment.data, providers, insurers)
  const populatedCorrections = populateAssessmentData(assessment.corrections, providers, insurers)

  const mergedData = mergeData(populatedData ?? {}, populatedCorrections ?? {})
  const mergedSignOnData = mergeAssessmentSignOnData(assessment)

  const midwifeId = getMidwifeId(mergedData)
  const payments = adminAssessmentData?.payments ?? practiceAssessmentData?.payments
  const populated: Omit<PopulatedAssessment, 'availableForms'> = {
    ...assessment,
    ...practiceAssessmentData,
    mergedData,
    signedOnDate: assessment.signedOnDate ?? null,
    isGynProfile: getIsGynProfile(assessment.name),
    archivedOn: assessment.archivedOn ?? null,
    adminStages: adminAssessmentData?.stages ?? [],
    practiceStages: practiceAssessmentData?.stages ?? [],
    adminStageHistory: adminAssessmentData?.stageHistory ?? [],
    practiceStageHistory: practiceAssessmentData?.stageHistory ?? [],
    adminArchivedHistory: assessment?.archivedHistory ?? [],
    practiceArchivedHistory: practiceAssessmentData?.archivedHistory ?? [],
    plans: populatePregnancyPlans(assessment, user.insurancePlans),
    midwifeId: practiceAssessmentData?.midwifeId || midwifeId,
    data: populatedData,
    corrections: populatedCorrections,
    payments,
  }

  const midwife = midwifeId ? providers[midwifeId] : undefined
  if (midwife) {
    mergedData.delivery.midwife = midwife
  }
  const urgent = getUrgentString(adminAssessmentData?.log)
  const withoutRedFlags: Omit<PopulatedAssessment, 'redFlags'> = {
    ...populated,
    ...assessmentSnippet,
    ...adminAssessmentData,
    miscarried: assessment.miscarried,
    miscarriedUpdatedBy: assessment.miscarriedUpdatedBy,
    miscarriedUpdatedByGroup: assessment.miscarriedUpdatedByGroup,
    miscarriedUpdatedOn: assessment.miscarriedUpdatedOn,
    miscarriedHistory: assessment.miscarriedHistory,
    delivery: assessment.delivery,
    plans: populated.plans,
    additionalPlansConfirmedBy: assessment.additionalPlansConfirmedBy,
    additionalPlansConfirmedOn: assessment.additionalPlansConfirmedOn,
    archivedOn:
      (practiceAssessmentData ? practiceAssessmentData.archivedOn : assessment.archivedOn) ?? null,
    status: getAssessmentStatus(true, populated, adminAssessmentData),
    adminAlerts: adminAssessmentData?.alerts,
    practiceAlerts: practiceAssessmentData?.alerts,
    isGynProfile: getIsGynProfile(assessment.name),
    mergedSignOnData,
    signedOnDate: assessment.signedOnDate ?? null,
    insuranceCallInRequired: getInsuranceCallInRequired(populated),
    insuranceCallInComplete: getInsuranceCallInComplete(populated),
    // selectedCoverage,
    // plans,
    // insuranceProvider,
    urgent,
    assessmentNote: adminAssessmentData?.assessmentNote || practiceAssessmentData?.assessmentNote,
    sharedNote: assessmentSnippet?.sharedNote || practiceAssessmentData?.sharedNote,
    sharedPaymentNote:
      assessmentSnippet?.sharedPaymentNote || practiceAssessmentData?.sharedPaymentNote,
    midwife,
    midwifeId: midwifeId ?? undefined,
    receivedPayments: objectToArray(payments?.received ?? {}),
    duePayments: objectToArray(payments?.due ?? {}),
    files: assessment.files ?? {},
    adminFiles: adminAssessmentData?.files ?? {},
    practiceFiles: practiceAssessmentData?.files ?? {},
  }
  return {
    ...withoutRedFlags,
    redFlags: getAssessmentRedFlags(withoutRedFlags),
  } as PopulatedAssessment
}
